/**
 * PATH: packages/ui/src/components/settings/settings-tab-sections/account-sections.tsx
 * PURPOSE: Build the "My Account" tab sections (Profile + Password + Security)
 * WHY: Extracted from buildSettingsTabs.tsx to keep files under 300 lines
 *
 * FLOW:
 *   ┌─────────────────────────┐     ┌────────────────────┐
 *   │ BuildSettingsTabsOptions │ → │ SettingsSection[]   │
 *   └─────────────────────────┘     └────────────────────┘
 *
 * DEPENDENCIES:
 *   - ProfileTab, PasswordTab, SecurityTab: rendered inside sections
 *   - SectionedContent types: SettingsSection
 * RELATED:
 *   - buildSettingsTabs.tsx: consumes this builder
 */

import { User, Lock, Shield } from 'lucide-react'
import { ProfileTab } from '../ProfileTab'
import { PasswordTab } from '../PasswordTab'
import { SecurityTab, type SecurityTabProps } from '../SecurityTab'
import type { SettingsSection } from '../SectionedContent'
import type { BuildSettingsTabsOptions } from '../buildSettingsTabs'
import { isUserAdmin } from '../utils'

/**
 * buildAccountSections — Profile, Password, Security
 * Accepts the full options object; extracts only what it needs.
 */
export function buildAccountSections(
  options: BuildSettingsTabsOptions,
): SettingsSection[] {
  const {
    user,
    apiClient,
    profileData,
    onProfileSave,
    onAvatarChange,
    profileContent,
    onPasswordChange,
    passwordContent,
    securityProps,
    isProfileLoading,
    isProfileSaving,
  } = options

  const resolvedSecurityProps: SecurityTabProps = {
    apiClient,
    user: user
      ? { id: user.id || 'current', email: user.email || '' }
      : undefined,
    // Org-wide MFA policy controls render only for admins. Derived from the
    // same RBAC-backed flag the rest of settings trusts; a host can still
    // override explicitly via securityProps.isAdmin.
    isAdmin: isUserAdmin(user),
    ...securityProps,
  }

  return [
    {
      id: 'profile',
      title: 'Profile',
      icon: User,
      description: 'Name, email, and avatar',
      content: profileContent || (
        <ProfileTab
          profile={profileData || {
            first_name: user?.first_name,
            last_name: user?.last_name,
            email: user?.email,
            organisation_name: user?.organisation_name,
            avatar_url: user?.avatar_url,
          }}
          isLoading={isProfileLoading}
          isSaving={isProfileSaving}
          onSave={onProfileSave}
          onAvatarChange={onAvatarChange}
          editable={!!onProfileSave}
        />
      ),
    },
    {
      id: 'password',
      title: 'Password',
      icon: Lock,
      description: 'Update your login credentials',
      content: passwordContent || <PasswordTab onChangePassword={onPasswordChange} />,
      defaultCollapsed: true,
    },
    {
      id: 'security',
      title: 'Security',
      icon: Shield,
      description: 'Two-factor authentication & active sessions',
      content: <SecurityTab {...resolvedSecurityProps} />,
      defaultCollapsed: true,
    },
  ]
}
